import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useHistory } from 'react-router-dom';
import { Table, Space, Input, Button, Dropdown, Menu } from 'antd';
import { SearchOutlined, MoreOutlined } from '@ant-design/icons';
import sum from 'lodash/sum';
import moment from 'moment';
import GlobalLoading from 'components/app/components/common/globalLoading/globalLoading';
import SideModal from 'components/app/common/sideModal/';
import { ExportPoll } from 'apis/rest/polls/ExportPoll';
import FileSaver from 'file-saver';
import { getFullname } from '../../polls';
import './endedTable.scss';
import { useSelector } from 'react-redux';
import PollResult from '../../pollResult';
import NoDataComponent from '../../../../../../../../ComponentsV2/CommonComponents/MobileView/noDataComponent/NoDataComponent';
import useScreenSize from 'components/app/utils/hooks/useScreenSize/useScreenSize';

const EndedTable = ({
    data,
    onRefreshData,
    pagination,
    loading,
    search,
    onClickDuplicatePoll,
    onClickDeletePoll,
    setSorter,
}) => {
    const { projectId, companyId } = useParams();
    const history = useHistory();
    const deviceType = useScreenSize();

    const [searchText, setSearchText] = useState('');
    const [sideModalVisibility, setSideModalVisibility] = useState(false);
    const [selectedPoll, setSelectedNotification] = useState(null);
    const sortDirections = ['descend', 'ascend', 'descend'];

    const { activeTimezone: timezone } = useSelector(
        (state) => state?.settings
    );

    useEffect(() => {
        const trimSearchText = searchText?.trim();
        search(trimSearchText);
    }, [searchText]);

    const handleGetExportPoll = async (pollId) => {
        try {
            const response = await ExportPoll(pollId, projectId);
            if (response) {
                FileSaver.saveAs(response, `${pollId}-poll-report.csv`);
            }
        } catch (error) {
            console.error('ERROR handleGetExportPoll', error);
        }
    };

    //Search part
    let searchInput;
    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({
            setSelectedKeys,
            selectedKeys,
            confirm,
            clearFilters,
        }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={(node) => {
                        searchInput = node;
                    }}
                    placeholder="Search"
                    value={selectedKeys[0]}
                    defaultValue={searchText}
                    onChange={(e) =>
                        setSelectedKeys(e.target.value ? [e.target.value] : [])
                    }
                    onPressEnter={() =>
                        handleSearch(selectedKeys, confirm, dataIndex)
                    }
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() =>
                            handleSearch(selectedKeys, confirm, dataIndex)
                        }
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined
                style={{ color: filtered ? '#1890ff' : undefined }}
            />
        ),
        onFilter: (value, record) =>
            record[dataIndex]
                ? record[dataIndex]
                      .toString()
                      .toLowerCase()
                      .includes(value.toLowerCase())
                : '',
        onFilterDropdownVisibleChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput?.select(), 100);
            }
        },
        render: (text) => text,
    });

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        const searchTextSelect = selectedKeys[0] ? selectedKeys[0] : searchText;
        if (!/\s/.test(searchTextSelect)) {
            confirm();
            setSearchText(searchTextSelect);
        }
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText('');
    };

    const handleChange = (a, b, sorter) => {
        const { field, order } = sorter;

        setSorter({
            sortOrder: order === 'ascend' ? 'ASC' : 'DESC',
            sortBy: field,
        });
    };

    const renderDropDownMenu = (item) => {
        return (
            <Menu>
                <Menu.Item key="0">
                    <a onClick={() => onClickDuplicatePoll(item.id)}>
                        Duplicate
                    </a>
                </Menu.Item>
                <Menu.Item key="1">
                    <a onClick={() => onClickDeletePoll(item.id)}>Delete</a>
                </Menu.Item>
            </Menu>
        );
    };

    const columns = [
        {
            title: 'Question',
            dataIndex: 'question',
            sorter: true,
            sortDirections,
            ...getColumnSearchProps('question'),
        },
        {
            title: 'Sent On',
            dataIndex: 'publishedTime',
            sorter: true,
            sortDirections,
            render: (publishedTime, item) => {
                if (!publishedTime) return null;
                return (
                    <span>
                        {moment(publishedTime)
                            .tz(timezone)
                            .format('DD MMMM YYYY [at] HH:mm')}
                    </span>
                );
            },
        },
        {
            title: (
                <span
                    className="tw-w-max tw-flex"
                    style={{ minWidth: '114px' }}
                >
                    Ended On
                </span>
            ),
            dataIndex: 'endTime',
            render: (endTime, item) => {
                if (!endTime) return null;
                return (
                    <span>
                        {' '}
                        {moment(endTime)
                            .tz(timezone)
                            .format('DD MMMM YYYY [at] HH:mm')}{' '}
                    </span>
                );
            },
        },
        {
            title: 'Target',
            dataIndex: 'target',
            sorter: true,
            sortDirections,
            render: (target, item) => {
                return (
                    <span>
                        {item?.targetType === 'project'
                            ? 'All Livepages'
                            : target?.length}
                    </span>
                );
            },
        },
        {
            title: (
                <span
                    className="tw-w-max tw-flex"
                    style={{ minWidth: '114px' }}
                >
                    Sent by
                </span>
            ),
            dataIndex: 'updatedBySponsor',
            render: (updatedBySponsor, item) => {
                return (
                    <span style={{ minWidth: '114px' }}>
                        {getFullname(updatedBySponsor || item?.updatedByUser)}
                    </span>
                );
            },
        },
        {
            title: (
                <span
                    className="tw-w-max tw-flex"
                    style={{ minWidth: '114px' }}
                >
                    Ended by
                </span>
            ),
            dataIndex: 'endedByUser',
            render: (endedByUser, item) => {
                return <span>{endedByUser}</span>;
            },
        },
        {
            title: 'Responses',
            dataIndex: 'reponses',
            render: (reponses, item) => {
                return <div>{item?.voters.length}</div>;
            },
        },
        {
            render: (item) => {
                return (
                    <div className="d-flex justify-content-end">
                        <button
                            className="myxp btn btn-sm btn-link"
                            onClick={() => {
                                setSideModalVisibility(true);
                                setSelectedNotification(item);
                            }}
                        >
                            {' '}
                            Report{' '}
                        </button>
                        <button
                            className="myxp btn btn-sm btn-link tw-flex md:tw-hidden"
                            onClick={() => onClickDuplicatePoll(item.id)}
                        >
                            Duplicate
                        </button>
                        <button
                            className="myxp btn btn-sm btn-link tw-flex md:tw-hidden text-danger"
                            onClick={() => onClickDeletePoll(item.id)}
                        >
                            Delete
                        </button>
                        <Dropdown
                            overlay={renderDropDownMenu(item)}
                            trigger={['click']}
                            className="tw-hidden sm:tw-block"
                        >
                            <MoreOutlined
                                onClick={(e) => e.preventDefault()}
                                style={{ fontSize: 19 }}
                                className="pt-2"
                            />
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    const globalLoadingComponent = {
        spinning: loading,
        indicator: (
            <GlobalLoading
                loadingText="Fetching data..."
                height={45}
                width={45}
            />
        ),
    };

    const renderPagination = (current, type, originalElement) => {
        return <a onClick={() => pagination(current)}>{originalElement}</a>;
    };

    return (
        <>
            {!loading && data?.polls.length ? (
                <Table
                    onChange={handleChange}
                    className="sponsors-company-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={data?.polls}
                    loading={globalLoadingComponent}
                    pagination={
                        data?.pagination?.totalPage > 1
                            ? {
                                  itemRender: renderPagination,
                                  pageSize: 10,
                                  total: data?.pagination?.total,
                                  current: data?.pagination?.currentPage,
                              }
                            : false
                    }
                    scroll={{ x: 767 }}
                />
            ) : (
                <NoDataComponent
                    loading={loading}
                    buttonText="Create poll"
                    description="No poll have been Created"
                    onClickHandler={() =>
                        history.push(
                            `/company/${companyId}/project/${projectId}/notification/create-poll`
                        )
                    }
                />
            )}

            <SideModal
                visibility={sideModalVisibility}
                setVisibility={(status) => {
                    setSideModalVisibility(status);
                    setSelectedNotification(null);
                }}
                title={
                    deviceType !== 'mobile'
                        ? `"${selectedPoll?.question}"`
                        : null
                }
                subTitle={
                    <>
                        {deviceType !== 'mobile' ? (
                            <span
                                className="badge badge-pill badge-secondary d-flex align-items-center"
                                style={{ width: 'fit-content' }}
                            >
                                ENDED
                            </span>
                        ) : (
                            <span className="tw-text-[18px] tw-font-semibold tw-text-mobileTextBlack">
                                Report
                            </span>
                        )}
                    </>
                }
            >
                <div className="d-flex justify-content-between">
                    <h3 className="poll-modal-title mt-2">
                        {sum(
                            selectedPoll?.options?.map((option) => option?.vote)
                        )}{' '}
                        Responses
                    </h3>
                    {deviceType !== 'mobile' && (
                        <a onClick={() => handleGetExportPoll(selectedPoll.id)}>
                            Export
                        </a>
                    )}
                </div>

                <PollResult data={selectedPoll} type="ended" />
                {deviceType !== 'mobile' ? (
                    <button
                        className="myxp btn btn-sm btn-primary btn-block mt-3"
                        onClick={() => {
                            setSideModalVisibility(false);
                            setSelectedNotification(null);
                        }}
                    >
                        Done
                    </button>
                ) : (
                    <button
                        className="tw-w-full tw-py-2 tw-px-4 tw-border tw-border-solid tw-border-blue-500 tw-rounded-md tw-bg-blue-500 tw-text-white tw-text-base tw-font-semibold tw-text-center tw-cursor-pointer tw-mb-2"
                        onClick={() => {
                            setSideModalVisibility(false);
                            setSelectedNotification(null);
                        }}
                    >
                        Done
                    </button>
                )}
            </SideModal>
        </>
    );
};

EndedTable.propTypes = {
    data: PropTypes.object,
    onRefreshData: PropTypes.func,
    pagination: PropTypes.func,
    search: PropTypes.func,
    onClickDuplicateNotification: PropTypes.func,
    onClickDeleteNotification: PropTypes.func,
    sendNotification: PropTypes.func,
    loading: PropTypes.bool,
    setSorter: PropTypes.func,
};

EndedTable.defaultProps = {
    data: null,
    loading: false,
};

export default EndedTable;
